/**
 * Dashboards resource — typed wrappers over `GET /v1/iot/dashboards[/{id}]`
 * plus a server→UI mapper.
 *
 * The server's `config` field is opaque JSON the UI lane owns — it *is*
 * a DashboardConfig minus the identity fields, which live on the envelope.
 * So the mapper is near-identity: hydrate `config`, then let the
 * envelope's id/name/updated_at win.
 */

import { api } from "@/lib/api-client";
import type { DashboardConfig } from "@/lib/dashboard/types";
import type { Dashboard, DashboardList } from "./types";

export const dashboards = {
  list: () => api.get<DashboardList>("/v1/iot/dashboards"),
  get: (id: string) => api.get<Dashboard>(`/v1/iot/dashboards/${id}`),
  /**
   * Create a dashboard. `id` is optional + client-owned (the SPA's
   * create/clone are synchronous, so the store mints a UUID and the server
   * upserts on it); `slug` is optional — the server derives a collision-free
   * one from the name + id. Mirrors `reports.create`.
   */
  create: (body: { id?: string; name: string; slug?: string; config?: unknown }) =>
    api.post<Dashboard>("/v1/iot/dashboards", body),
  /**
   * Patch name and/or the opaque config blob. `expected_updated_at` is the optional precondition: the `updated_at` the
   * caller read the dashboard at; the platform answers 409 board_changed and writes nothing when it moved on.
   */
  update: (id: string, body: { name?: string; config?: unknown; expected_updated_at?: string }) =>
    api.patch<Dashboard>(`/v1/iot/dashboards/${id}`, body),
  /** Delete a dashboard (204; idempotent server-side). */
  remove: (id: string) => api.delete<void>(`/v1/iot/dashboards/${id}`),
};

/**
 * Mapper exists because the server wraps the UI-owned DashboardConfig in
 * an envelope. Near-identity. Server dashboards are always user-owned
 * ("my"); pack dashboards stay client-side until
 * `POST /v1/platform/packs/foundry/enable` seeds them server-side
 * (`docs/07 § 5.5`).
 */
export function toDashboardConfig(d: Dashboard): DashboardConfig {
  const cfg = (d.config && typeof d.config === "object"
    ? (d.config as Partial<DashboardConfig>)
    : {}) as Partial<DashboardConfig>;

  return {
    ...cfg,
    id: d.id,
    name: d.name,
    source: cfg.source === "shared" ? "shared" : "my",
    owner: cfg.owner ?? "You",
    updatedAt: d.updated_at,
    cols: cfg.cols ?? 12,
    rowHeight: cfg.rowHeight ?? 40,
    // The blob is kept as it was sent: a board's widgets that are not a list of objects blanked the Dashboards index
    // (every card reads them) and the board (editor review, 2026-10-30).
    widgets: Array.isArray(cfg.widgets) ? cfg.widgets.filter((w) => w !== null && typeof w === "object") : [],
  } as DashboardConfig;
}
